import React, { useState } from 'react';
import { ChannelBadge } from '../components/common/Badge';
import { Facebook, Search, Video, Mail } from 'lucide-react';

export function Dashboard({ leads = [], onSelectLead, globalSearch = '', showToast }) {
  const [selectedChannel, setSelectedChannel] = useState('all');

  // Channel Options
  const channels = [
    { id: 'all', label: 'All channels', color: '#64748B' },
    { id: 'Facebook', label: 'Facebook', color: '#1877F2' },
    { id: 'Google', label: 'Google', color: '#4285F4' },
    { id: 'TikTok', label: 'TikTok', color: '#000000' },
    { id: 'Referral', label: 'Email / Referral', color: '#D97706' }
  ];

  // Channel Metrics Cards Data
  const channelCards = [
    {
      id: 'Facebook',
      name: 'Facebook',
      icon: Facebook,
      iconColor: '#1877F2',
      color: '#1877F2',
      leads: '175',
      spent: '฿116.8K',
      cpl: '฿667.5',
      metricLabel: 'Cost / lead',
      metricValue: '฿667.5',
      fillPercent: 75
    },
    {
      id: 'Google',
      name: 'Google',
      icon: Search,
      iconColor: '#EA4335',
      color: '#4285F4',
      leads: '128',
      spent: '฿98.4K',
      cpl: '฿769.0',
      metricLabel: 'Cost / lead',
      metricValue: '฿769.0',
      fillPercent: 60
    },
    {
      id: 'TikTok',
      name: 'TikTok',
      icon: Video,
      iconColor: '#000000',
      color: '#000000',
      leads: '74',
      spent: '฿41.2K',
      cpl: '฿557.0',
      metricLabel: 'Cost / lead',
      metricValue: '฿557.0',
      fillPercent: 40
    },
    {
      id: 'Referral',
      name: 'Email / Referral',
      icon: Mail,
      iconColor: '#D97706',
      color: '#D97706',
      leads: '35',
      spent: 'Organic',
      cpl: '68%',
      metricLabel: 'Qualify rate',
      metricValue: '68%',
      fillPercent: 85
    }
  ];

  // Case-insensitive channel filter fix
  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      globalSearch === '' ||
      lead.name.toLowerCase().includes(globalSearch.toLowerCase()) ||
      lead.company.toLowerCase().includes(globalSearch.toLowerCase()) ||
      lead.id.toLowerCase().includes(globalSearch.toLowerCase());

    if (!matchesSearch) return false;
    if (selectedChannel === 'all') return true;

    const leadChan = (lead.channel || '').toLowerCase();
    const selChan = selectedChannel.toLowerCase();

    if (selChan === 'referral') {
      return leadChan === 'referral' || leadChan === 'email' || leadChan === 'website';
    }

    return leadChan === selChan;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
      {/* Row 1: Top Channel Filter Pills */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflowX: 'auto', paddingBottom: '2px' }}>
        {channels.map((ch) => {
          const isSelected = selectedChannel === ch.id;
          return (
            <button
              key={ch.id}
              onClick={() => setSelectedChannel(ch.id)}
              style={{
                padding: '6px 16px',
                borderRadius: '9999px',
                border: isSelected ? '1px solid #09090B' : '1px solid #E4E4E7',
                backgroundColor: isSelected ? '#090D16' : '#FFFFFF',
                color: isSelected ? '#FFFFFF' : '#374151',
                fontSize: '13px',
                fontWeight: isSelected ? 700 : 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: isSelected ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s ease'
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: isSelected ? '#2cabe1' : ch.color }}></span>
              <span>{ch.label}</span>
            </button>
          );
        })}
      </div>

      {/* Row 2: 5 KPI Summary Cards */}
      <div className="kpi-grid-container">
        <div className="card" style={{ padding: '16px 18px', backgroundColor: '#FFFFFF' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', textTransform: 'uppercase' }}>TOTAL LEADS</div>
          <div style={{ fontSize: '26px', fontWeight: 800, color: '#09090B', marginTop: '4px', letterSpacing: '-0.02em' }}>412</div>
          <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#83c341', marginTop: '4px' }}>↑ 18% vs prior period</div>
        </div>

        <div className="card" style={{ padding: '16px 18px', backgroundColor: '#FFFFFF' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', textTransform: 'uppercase' }}>QUALIFIED</div>
          <div style={{ fontSize: '26px', fontWeight: 800, color: '#09090B', marginTop: '4px', letterSpacing: '-0.02em' }}>186</div>
          <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#83c341', marginTop: '4px' }}>45% qualify rate</div>
        </div>

        <div className="card" style={{ padding: '16px 18px', backgroundColor: '#FFFFFF' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', textTransform: 'uppercase' }}>SITE VISITS BOOKED</div>
          <div style={{ fontSize: '26px', fontWeight: 800, color: '#09090B', marginTop: '4px', letterSpacing: '-0.02em' }}>64</div>
          <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#83c341', marginTop: '4px' }}>↑ 9% vs prior period</div>
        </div>

        <div className="card" style={{ padding: '16px 18px', backgroundColor: '#FFFFFF' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', textTransform: 'uppercase' }}>LEASES SIGNED</div>
          <div style={{ fontSize: '26px', fontWeight: 800, color: '#09090B', marginTop: '4px', letterSpacing: '-0.02em' }}>21</div>
          <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#83c341', marginTop: '4px' }}>33% visit → lease</div>
        </div>

        <div className="card" style={{ padding: '16px 18px', backgroundColor: '#FFFFFF' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', textTransform: 'uppercase' }}>BLENDED COST / LEAD</div>
          <div style={{ fontSize: '26px', fontWeight: 800, color: '#09090B', marginTop: '4px', letterSpacing: '-0.02em' }}>฿612</div>
          <div style={{ fontSize: '11.5px', fontWeight: 600, color: '#83c341', marginTop: '4px' }}>↓ 6% vs prior period</div>
        </div>
      </div>

      {/* Row 3: Channels feeding this CRM Section */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#09090B' }}>Channels feeding this CRM</h3>
        </div>

        <div className="grid-4">
          {channelCards.map((card) => {
            const isCardActive = selectedChannel.toLowerCase() === card.id.toLowerCase();
            const IconComponent = card.icon;
            return (
              <div
                key={card.id}
                onClick={() => setSelectedChannel(isCardActive ? 'all' : card.id)}
                className="card"
                style={{
                  padding: '18px 20px',
                  backgroundColor: '#FFFFFF',
                  border: isCardActive ? '2px solid #2cabe1' : '1px solid #E4E4E7',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  position: 'relative'
                }}
              >
                {/* Header with Real SVG Brand Icon */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '6px', backgroundColor: '#F4F4F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconComponent size={16} color={card.iconColor} />
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#09090B' }}>{card.name}</span>
                </div>

                {/* Metrics */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12.5px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#71717A' }}>Leads</span>
                    <span style={{ fontWeight: 700, color: '#09090B' }}>{card.leads}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#71717A' }}>Amount spent</span>
                    <span style={{ fontWeight: 600, color: '#09090B' }}>{card.spent}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#71717A' }}>{card.metricLabel}</span>
                    <span style={{ fontWeight: 700, color: '#09090B' }}>{card.metricValue}</span>
                  </div>
                </div>

                {/* Color Progress Bar */}
                <div style={{ width: '100%', height: '4px', backgroundColor: '#F4F4F5', borderRadius: '9999px', marginTop: '14px', overflow: 'hidden' }}>
                  <div style={{ width: `${card.fillPercent}%`, height: '100%', backgroundColor: card.color, borderRadius: '9999px' }}></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Row 4: Full-Width Lead Pipeline Section */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E4E4E7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#09090B' }}>Lead pipeline</h3>
          </div>
          <span style={{ fontSize: '12.5px', color: '#71717A' }}>
            {filteredLeads.length} leads shown
          </span>
        </div>

        <div className="table-responsive">
          <table className="custom-table">
            <thead>
              <tr>
                <th>LEAD</th>
                <th>CHANNEL</th>
                <th>BUSINESS TYPE</th>
                <th>SPACE NEEDED</th>
                <th>TIMELINE</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => (
                <tr
                  key={lead.id}
                  onClick={() => onSelectLead && onSelectLead(lead)}
                  style={{ cursor: 'pointer' }}
                >
                  {/* LEAD (Name & RPM ID) */}
                  <td>
                    <div style={{ fontWeight: 700, color: '#09090B', fontSize: '13.5px' }}>{lead.company || lead.name}</div>
                    <div style={{ fontSize: '11.5px', color: '#94A3B8', marginTop: '1px', fontFamily: 'var(--font-mono)' }}>{lead.id}</div>
                  </td>

                  {/* CHANNEL BADGE */}
                  <td>
                    <ChannelBadge channel={lead.channel} />
                  </td>

                  {/* BUSINESS TYPE */}
                  <td style={{ color: '#374151', fontSize: '13px' }}>
                    {lead.businessType || 'Law firm'}
                  </td>

                  {/* SPACE NEEDED */}
                  <td style={{ color: '#374151', fontSize: '13px', fontWeight: 500 }}>
                    {lead.space || lead.spaceNeeded || 'Office, 100-140 sqm'}
                  </td>

                  {/* TIMELINE */}
                  <td style={{ color: '#52525B', fontSize: '13px' }}>
                    {lead.timeline || 'Within 2 months'}
                  </td>

                  {/* STATUS PILL */}
                  <td>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '9999px',
                        fontSize: '11.5px',
                        fontWeight: 600,
                        backgroundColor:
                          lead.status === 'qualified' ? '#EBF8FC' :
                            lead.status === 'proposal' ? '#FFFBEB' :
                              lead.status === 'leased' ? '#F2F9EC' : '#F4F4F5',
                        color:
                          lead.status === 'qualified' ? '#1e92c4' :
                            lead.status === 'proposal' ? '#D97706' :
                              lead.status === 'leased' ? '#047857' : '#52525B'
                      }}
                    >
                      {lead.status === 'qualified' ? 'Qualified' :
                        lead.status === 'proposal' ? 'Site Visit' :
                          lead.status === 'leased' ? 'Leased' : 'Contacted'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
